import { matchRoutes } from "react-router-dom";
import { protectedRoutes } from "components/routes/routes";
import { translate } from "services/general";
import { HAS_PERMISSION } from "services/user";
import { findSidebarTrail, hasSidebarLink } from "./sidebarTrail";
import { CRUMB_KIND } from "./constants";
import { buildRouteUrl, stripTrailingSlash } from "./routeUrl";

const HOME_ROUTE_PATH = "home";
// A guard against a parent cycle in routes.js, far above the deepest real trail.
const MAX_DEPTH = 12;

const ROUTES_BY_PATH = Object.fromEntries(
  protectedRoutes.map((route) => [route.path, route])
);
const MATCHABLE_ROUTES = protectedRoutes.map((route) => ({
  path: `/:type/${route.path}`,
  route,
}));

function splitPath(path) {
  const [routePath, query] = path.split("?");
  return { path: routePath, query: query ? `?${query}` : "" };
}

function canOpen(route) {
  return !route.permissionId || HAS_PERMISSION(route.permissionId);
}

// A page's parent: the one a page registered through useBreadcrumbParent, else the
// one declared in routes.js.
function parentOf(route, url, parents) {
  const override = parents[url];
  if (override) {
    return { ...splitPath(override.path), params: override.params };
  }
  return route.breadcrumb?.parent ? splitPath(route.breadcrumb.parent) : null;
}

// From the page up through each parent to a page with none. Each level inherits the
// params the level below it was built with, so a registered parent's params reach
// the levels above it too.
function resolvePageChain(route, params, pathname, parents) {
  let current = { route, params, url: pathname, query: "" };
  const chain = [current];
  let parentRef = parentOf(route, pathname, parents);

  while (parentRef && chain.length < MAX_DEPTH) {
    const parentRoute = ROUTES_BY_PATH[parentRef.path];
    const parentParams = { ...current.params, ...parentRef.params };
    const url = parentRoute && buildRouteUrl(parentRoute.path, parentParams);
    if (!url) break;

    current = {
      route: parentRoute,
      params: parentParams,
      url,
      query: parentRef.query,
    };
    chain.unshift(current);
    parentRef = parentOf(parentRoute, url, parents);
  }
  return chain;
}

function pageLabel(item, sidebarLeaf, labels, t) {
  if (labels[item.url]) return labels[item.url];
  if (sidebarLeaf) return translate(sidebarLeaf.text, sidebarLeaf.en_text);
  if (item.route.breadcrumb?.label) {
    return t(item.route.breadcrumb.label, item.params);
  }
  return item.route.path;
}

// A sidebar group holding one page of the same name ("Settings › Settings") shows
// once. The sidebar entries are compared in both languages, so the trail keeps one
// shape whichever language is on screen.
function sectionRepeatsLeaf(section, leaf) {
  return (
    Boolean(leaf) &&
    (section.text === leaf.text || section.en_text === leaf.en_text)
  );
}

// The pages from the top one down to the current one, or null off the route table.
function pageChain(currentPath, parents) {
  const [match] = matchRoutes(MATCHABLE_ROUTES, currentPath) ?? [];
  if (!match) return null;

  const { route } = match.route;
  // `:type` is the language segment; a route declaring its own `:type` would
  // otherwise overwrite it in every crumb's URL.
  const params = { ...match.params, type: currentPath.split("/")[1] };
  return {
    params,
    chain: resolvePageChain(route, params, currentPath, parents),
  };
}

/**
 * The trail for the current URL: Home, the sidebar groups above the top page, then
 * every page from the top one down to the current one. The last crumb has no url.
 * `contributions` are what the page registered through the useBreadcrumb* hooks.
 */
export function buildTrail({ pathname, search, contributions, t }) {
  const currentPath = stripTrailingSlash(pathname);
  const { labels, parents, tab } = contributions;
  const resolved = pageChain(currentPath, parents);
  if (!resolved) return [];
  const { params, chain } = resolved;

  const top = chain[0];
  const isHomeTrail = top.route.path === HOME_ROUTE_PATH;
  const topQuery = chain.length === 1 ? search : top.query;
  const { sections, leaf } = isHomeTrail
    ? { sections: [], leaf: null }
    : findSidebarTrail(top.url, topQuery);

  const homeRoute = ROUTES_BY_PATH[HOME_ROUTE_PATH];
  const homeCrumb = {
    key: "home",
    kind: CRUMB_KIND.HOME,
    label: t("breadcrumbs.home"),
    url: canOpen(homeRoute) ? buildRouteUrl(HOME_ROUTE_PATH, params) : null,
  };

  const lastSection = sections[sections.length - 1];
  const visibleSections =
    lastSection && sectionRepeatsLeaf(lastSection, leaf)
      ? sections.slice(0, -1)
      : sections;
  const sectionCrumbs = visibleSections.map((entry) => ({
    key: `section-${entry.en_text}`,
    kind: CRUMB_KIND.SECTION,
    label: translate(entry.text, entry.en_text),
    entry,
    activePath: leaf?.path,
  }));

  const pageCrumbs = (isHomeTrail ? chain.slice(1) : chain).map((item) => ({
    key: item.url,
    kind: CRUMB_KIND.PAGE,
    label: pageLabel(item, item === top ? leaf : null, labels, t),
    url: canOpen(item.route) ? `${item.url}${item.query}` : null,
  }));

  const tabCrumb =
    tab?.url === currentPath
      ? [{ key: `${currentPath}-tab`, kind: CRUMB_KIND.PAGE, label: tab.label }]
      : [];

  const crumbs = [homeCrumb, ...sectionCrumbs, ...pageCrumbs, ...tabCrumb];

  // The page itself is plain text, unless a tab crumb follows it.
  const last = crumbs[crumbs.length - 1];
  crumbs[crumbs.length - 1] = { ...last, url: null };
  return crumbs;
}

/** The URL of every page in the trail, the current one included. */
export function trailPageUrls(pathname, parents) {
  const resolved = pageChain(stripTrailingSlash(pathname), parents);
  return resolved ? resolved.chain.map((item) => item.url) : [];
}

// Back's level. Sidebar groups (menus) and pages the user may not open have no url.
export function parentCrumbUrl(crumbs) {
  for (let i = crumbs.length - 2; i >= 0; i -= 1) {
    if (crumbs[i].url) return crumbs[i].url;
  }
  return null;
}

if (import.meta.env.DEV) {
  const unnamed = protectedRoutes
    .filter((route) => !route.breadcrumb?.label && !hasSidebarLink(route.path))
    .map((route) => route.path);
  const unknownParents = protectedRoutes
    .filter(
      (route) =>
        route.breadcrumb?.parent &&
        !ROUTES_BY_PATH[splitPath(route.breadcrumb.parent).path]
    )
    .map((route) => route.path);

  if (unnamed.length) {
    console.warn(
      "[breadcrumbs] Routes with no sidebar link and no breadcrumb.label:",
      unnamed
    );
  }
  if (unknownParents.length) {
    console.warn(
      "[breadcrumbs] Routes whose breadcrumb.parent is not a declared path:",
      unknownParents
    );
  }
}
